import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Spinner } from 'reactstrap';
import './styles.scss';
import * as Yup from 'yup';
import Field from '../../../../widgets/Field';
import { accountSelector } from '~redux/account/selectors/account';
import { authUserSelector } from '~redux/auth/selectors/authUser';
import { brandInfoSelector } from '~redux/brand/selectors';
import { getBrandInfo } from '~redux/brand/thunk/details';
import { buyerInfoSelector } from '~redux/buyer/selectors';
import { sendMessage } from '~redux/messages/thunk';
import { Form, Formik } from 'formik';
import { useThunk } from '~utils/hooks';
import { getResizeImageUrl } from '~utils/image';

type MessageModalProps = {
  brandId: string;
  productName?: string;
  isShow: boolean;
  close: () => void;
  tel?: boolean;
};

const MessageModal: React.FC<MessageModalProps> = ({
  brandId,
  productName,
  isShow,
  close,
  tel,
}) => {
  const dispatch = useDispatch();
  const user = useSelector(authUserSelector);
  const account = useSelector(accountSelector);
  const brand = useSelector(brandInfoSelector);
  const buyerInfo = useSelector(buyerInfoSelector);
  const [message, setMessage] = useState('');
  const [submit, loading, complete] = useThunk(sendMessage);

  const onSubmit = useCallback(
    (values?: any) => {
      if (!user || !brand?.brand_owner) {
        return;
      }
      let msg = message;
      if (tel) {
        msg = `【自動送信】バイヤー様から電話でのご連絡がリクエストされました。
・会社名：${values.companyName}
・店舗名：${values.shopName}
・担当者名：${values.buyerName}
・電話番号：${values.tel}
・問い合わせ内容：${values.description}
`;
      }
      submit(msg, productName || '', user.attributes.sub, {
        buyer_owner: user.attributes.sub,
        brand_id: brandId,
        brand_owner: brand.brand_owner,
        owners: [user.attributes.sub, brand.brand_owner],
      });
    },
    [user, message, brandId, brand?.brand_owner]
  );

  useEffect(() => {
    if (brandId && isShow) {
      dispatch(getBrandInfo(brandId));
    }
  }, [brandId, isShow]);

  useEffect(() => {
    if (!complete) {
      return;
    }
    setTimeout(close, 500);
  }, [complete]);

  if (!user || !brand) {
    return null;
  }

  if (tel && (!account || !buyerInfo)) {
    return null;
  }

  return (
    <Modal show={isShow} onHide={close} contentClassName="message-modal">
      <Modal.Header>
        <Modal.Title className="message-modal-title">
          {brand.iconUrl && (
            <img
              src={getResizeImageUrl(brand.iconUrl, 60, 60)}
              alt="brand icon"
            />
          )}
          {!tel && (
            <>
              <span translate="no">{brand.brand_name}</span>
              へメッセージを送信する
            </>
          )}
          {tel && `ブランド担当者から電話で話を聞く`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!tel && (
          <textarea
            rows={10}
            className="message-modal-text"
            placeholder="メッセージを入力してください（5文字から送信可能です）"
            onInput={(e) => setMessage(e.currentTarget.value)}
            maxLength={2000}
          ></textarea>
        )}
        {tel && (
          <Formik
            onSubmit={onSubmit}
            initialValues={{
              companyName: account!.company_name,
              shopName: buyerInfo!.shop_name,
              buyerName: `${account!.last_name}${account!.first_name}`,
              tel: buyerInfo!.shipping_address?.phone_number,
              description: '',
            }}
            validationSchema={Yup.object().shape({
              companyName: Yup.string()
                .required('会社名を入力してください')
                .max(100, '100文字以内で入力してください'),
              shopName: Yup.string()
                .required('店舗名を入力してください')
                .max(100, '100文字以内で入力してください'),
              buyerName: Yup.string()
                .required('担当者名を入力してください')
                .max(100, '100文字以内で入力してください'),
              tel: Yup.string()
                .required('電話番号を入力してください')
                .max(30, '30文字以内で入力してください'),
              description: Yup.string().max(
                100,
                '100文字以内で入力してください'
              ),
            })}
            enableReinitialize
          >
            {() => (
              <Form>
                <div className="mb-4">
                  <Field name="companyName" label="会社名" />
                </div>
                <div className="mb-4">
                  <Field name="shopName" label="店舗名" />
                </div>
                <div className="mb-4">
                  <Field name="buyerName" label="担当者名" />
                </div>
                <div className="mb-4">
                  <Field name="tel" label="電話番号" />
                </div>
                <div className="mb-4">
                  <Field
                    name="description"
                    label="問い合わせ内容"
                    placeholder="ブランドについて、商品について、取引について"
                  />
                </div>
                <Button
                  style={{ backgroundColor: '#333', borderRadius: '0' }}
                  disabled={loading}
                  block
                >
                  {loading && <Spinner size="sm" className="mr-2" />}
                  {!complete && '電話で話を聞く'}
                  {complete && 'リクエストを送信しました！'}
                </Button>
                <small>※上記の情報がブランドへ送信されます</small>
              </Form>
            )}
          </Formik>
        )}
      </Modal.Body>
      {!tel && (
        <Modal.Footer className="message-modal-footer">
          <Button
            style={{ backgroundColor: '#333', borderRadius: '0' }}
            onClick={() => onSubmit()}
            disabled={(message?.length ?? 0) < 5}
            block
          >
            {loading && <Spinner size="sm" className="mr-2" />}
            {!complete && '送信'}
            {complete && 'メッセージを送信しました！'}
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default MessageModal;
